import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Flex, Link, Spinner, Text, Textarea, useToast } from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { FiAlertTriangle, FiArrowLeft, FiChevronDown, FiChevronRight, FiChevronUp, FiSend } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { askLay, LayError } from "../../lib/layChat";
import {
  buildLaySummary, connectReason, connectToAgent, errorCodeOf, fetchTopicFaq, fetchTopicTree, logPath, startPath, topicContext,
} from "../../lib/supportGuide";
import { isImpersonating } from "../../lib/policyConsentRules";
import { tt } from "../../i18n/tt";

/**
 * 문의 길잡이 (2026-10-28 · docs/ops/ops-chat-messenger-plan.md §1-2 · 시안 2~5)
 *   ① 분류 → ② 세부 → ③ 자주 묻는 질문 → ④ 레이에게 직접 질문 → ⑤ 「아직 해결 안 됐어요」 이유 → (조건) 상담사 연결
 *   · 상담사 연결은 서버가 판정한다(ops_chat_connect). 여기 connectReason() 은 버튼을 보여 줄지만 정한다.
 *   · 급한 문제 세부 · 레이 모름 · 레이 장애는 바로 연결 확인으로 간다(D19 · D13 · D14).
 *   · onConnected(roomId) — 방이 열리면 부르는 쪽이 대화방을 연다.
 *   · 회원으로 보기 중에는 연결하지 않는다(보기만).
 */
const steps = () => [tt("a.a8d523c3", "분류"), tt("a.1eae04bf", "세부"), tt("a.8563a5ea", "자주 묻는 질문"), tt("a.54c22d67", "레이")];
const reasons = () => [
  { value: "account", title: tt("a.33ea1d65", "내 계정·내 채널 상황을 봐야 해요"), hint: tt("a.dde71d66", "레이는 내 정산·채널 상태를 볼 수 없어요 → 상담사와 바로 연결") },
  { value: "wrong", title: tt("a.e9c02fd6", "답이 틀린 것 같아요"), hint: tt("a.2f6a6056", "레이가 한 번 더 찾아볼게요") },
  { value: "unclear", title: tt("a.331dc75e", "이해가 잘 안 돼요"), hint: tt("a.fb189607", "레이가 더 쉽게 다시 설명할게요") },
];
const followUp = (r) => ({
  wrong: tt("a.f17304ff", "앞의 답이 제 상황과 맞지 않는 것 같아요. 가이드를 다시 확인해서 답해 주세요."),
  unclear: tt("a.5a2b83c7", "앞의 답을 더 쉽고 짧게 다시 설명해 주세요."),
}[r]);

function StepBar({ at }) {
  return (
    <Flex gap={1} mb={3} aria-label={tt("a.5fc5f8a8", "문의 단계")}>
      {steps().map((s, i) => (
        <Box key={s} flex="1" textAlign="center" fontSize="11px" py={1} borderRadius="6px"
          bg={i < at ? "brand.50" : i === at ? "brand.500" : "neutral.100"}
          color={i < at ? "brand.700" : i === at ? "white" : "neutral.500"} fontWeight={i === at ? 700 : 500}
          aria-current={i === at ? "step" : undefined}>
          {s}
        </Box>
      ))}
    </Flex>
  );
}

// 레이 답·자주 묻는 질문 답 — 레이 위젯(LayWidget)과 같은 마크다운 표현
const mdComponents = {
  p: (p) => <Text fontSize="14px" lineHeight="1.65" mb={1.5} sx={{ "&:last-child": { mb: 0 } }} {...p} />,
  ol: (p) => <Box as="ol" pl={5} mb={1.5} fontSize="14px" lineHeight="1.65" {...p} />,
  ul: (p) => <Box as="ul" pl={5} mb={1.5} fontSize="14px" lineHeight="1.65" {...p} />,
  li: (p) => <Box as="li" mb={0.5} {...p} />,
  strong: (p) => <Text as="strong" fontWeight="600" {...p} />,
  a: (p) => <Link color="brand.700" textDecoration="underline" textUnderlineOffset="2px" isExternal {...p} />,
  code: (p) => <Text as="code" fontSize="0.9em" bg="blackAlpha.100" px={1} borderRadius="3px" {...p} />,
};

function LayText({ content }) {
  return <ReactMarkdown components={mdComponents}>{String(content || "")}</ReactMarkdown>;
}

export default function SupportGuide({ onConnected, onClose, compact = false }) {
  const toast = useToast();
  const location = useLocation();
  const impersonating = isImpersonating();
  const [tree, setTree] = useState(null);
  const [treeError, setTreeError] = useState(false);
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState(null);
  const [sub, setSub] = useState(null);
  const [faq, setFaq] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [faqDone, setFaqDone] = useState(false);
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [asking, setAsking] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [solved, setSolved] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState("");
  const [connecting, setConnecting] = useState(false);
  const pathRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchTopicTree().then((t) => { if (alive) setTree(t); }).catch(() => { if (alive) { setTree([]); setTreeError(true); } });
    return () => { alive = false; };
  }, []);

  const ensurePath = useCallback(async () => {
    if (pathRef.current) return pathRef.current;
    pathRef.current = await startPath();
    return pathRef.current;
  }, []);

  // 길 기록 — 화면 거울(events)은 바로 바꾸고 서버 기록은 뒤에서. 실패해도 길잡이는 계속된다(연결 때 서버가 다시 판정)
  const record = useCallback(async (kind, extra = {}, mirror = {}) => {
    setEvents((prev) => [...prev, { kind, ...mirror }]);
    try {
      const id = await ensurePath();
      await logPath(id, kind, extra);
    } catch { /* 연결 판정 때 서버 기록이 모자라면 not_allowed 로 알려 준다 */ }
  }, [ensurePath]);

  const reason = useMemo(() => connectReason(events), [events]);
  useEffect(() => { setTimeout(() => bottomRef.current?.scrollIntoView?.({ block: "end" }), 0); }, [messages, reasonOpen, confirming]);

  const pickCategory = (c) => {
    setCategory(c);
    record("topic", { topic: c.key });
    if (!c.children?.length) { setStep(3); return; }   // 「그 밖에」 — 바로 레이
    setStep(1);
  };

  const pickSub = async (s) => {
    setSub(s);
    await record("subtopic", { topic: s.key }, { topicIsUrgent: Boolean(s.is_urgent) });
    if (s.is_urgent) { setConfirming(true); setStep(3); return; }
    setStep(2);
    setFaq(null);
    try { setFaq(await fetchTopicFaq(s.key)); } catch { setFaq([]); }
  };

  const skipTopic = () => { record("skip_topic"); setStep(3); };

  const toggleFaq = (f) => {
    if (openFaq === f.entry_id) { setOpenFaq(null); return; }
    setOpenFaq(f.entry_id);
    record("faq_open", { topic: sub?.key, kbEntryId: f.entry_id });
  };

  const ask = async (text, { followUp = false } = {}) => {
    const q = String(text || "").trim();
    if (!q || asking) return;
    setAsking(true);
    setReasonOpen(false);
    setSolved(false);
    setMessages((prev) => [...prev, { role: "user", content: q, followUp }]);
    if (events.every((e) => e.kind !== "ask_lay")) record("ask_lay", { topic: sub?.key || category?.key || null });
    try {
      const ctx = [topicContext(category, sub), location.pathname ? `경로 ${location.pathname}` : ""].filter(Boolean).join(" · ");
      const res = await askLay({ message: q, conversationId, context: ctx, topicSlugs: sub?.guide_slugs || [] });
      setConversationId(res.conversationId);
      const unknown = res.escalation === "offer";
      setMessages((prev) => [...prev, { role: "lay", content: res.content, unknown, messageId: res.messageId }]);
      const ref = res.conversationId && res.messageId ? `${res.conversationId}:${res.messageId}` : null;
      await record(unknown ? "lay_unknown" : "lay_answer", { layRef: ref });
      if (unknown) setConfirming(true);
    } catch (err) {
      const down = !(err instanceof LayError) || err.status === 0 || err.status >= 500;
      setMessages((prev) => [...prev, { role: "lay", content: down ? tt("a.4f2b88b2", "지금 레이가 답하기 어려워요. 상담사와 바로 연결해 드릴게요.") : (err.content || tt("a.45cb680e", "다시 시도해 주세요.")), error: true }]);
      if (down) { await record("lay_error"); setConfirming(true); }
    } finally {
      setAsking(false);
    }
  };

  const markSolved = () => { setSolved(true); record("lay_yes"); };

  const chooseReason = async (r) => {
    setReasonOpen(false);
    await record("lay_not_yet", { reason: r }, { reason: r });
    if (r === "account") { setConfirming(true); return; }
    // 두 번째 「아직」이면 서버 조건(레이 답 2 · 아직 2)이 차서 연결로 간다
    const next = connectReason([...events, { kind: "lay_not_yet", reason: r }]);
    if (next) { setConfirming(true); return; }
    ask(followUp(r), { followUp: true });
  };

  const connect = async () => {
    if (impersonating) { toast({ title: tt("a.abeefc2c", "회원으로 보기 중에는 연결할 수 없어요."), status: "warning" }); return; }
    setConnecting(true);
    try {
      const id = await ensurePath();
      const res = await connectToAgent(id, note.trim(), buildLaySummary(messages));
      onConnected?.(res?.room_id);
    } catch (err) {
      const code = errorCodeOf(err);
      const text = {
        not_allowed: tt("a.71c2a086", "레이에게 먼저 물어봐 주세요. 그래도 해결이 안 되면 상담사와 연결해 드릴게요."),
        rate_limited: tt("a.af9b08e6", "오늘은 상담사 연결을 많이 요청했어요. 내일 다시 시도해 주세요."),
        path_used: tt("a.eb2fab12", "이미 연결했던 문의예요. 처음부터 다시 골라 주세요."),
        disabled: tt("a.e2826836", "지금은 상담사 연결을 쓸 수 없어요."),
      }[code] || tt("a.bae4fa4c", "연결하지 못했어요. 잠시 뒤 다시 시도해 주세요.");
      toast({ title: text, status: "error" });
      if (code === "path_used") { pathRef.current = null; setEvents([]); setConfirming(false); setStep(0); }
    } finally {
      setConnecting(false);
    }
  };

  const back = () => {
    setConfirming(false);
    setReasonOpen(false);
    if (step === 3) { setStep(sub ? 2 : category?.children?.length ? 1 : 0); return; }
    if (step === 2) { setStep(1); setSub(null); return; }
    if (step === 1) { setStep(0); setCategory(null); }
  };

  const lastLay = [...messages].reverse().find((m) => m.role === "lay");
  const showAnswerButtons = step === 3 && lastLay && !lastLay.error && !lastLay.unknown && !asking && !confirming && !solved;

  if (!tree) return <Flex justify="center" py={10}><Spinner color="brand.500" /></Flex>;

  return (
    <Box>
      <Flex align="center" gap={2} mb={2}>
        {step > 0 && <Button size="sm" variant="ghost" leftIcon={<FiArrowLeft />} onClick={back} aria-label={tt("a.dec9fc77", "이전 단계")}>{tt("a.cb1dc9f2", "이전")}</Button>}
        <Text fontSize="15px" fontWeight="700" flex="1">
          {step === 0 && tt("a.d43450e6", "어떤 내용이 궁금하세요?")}
          {step === 1 && category?.label}
          {step === 2 && sub?.label}
          {step === 3 && (sub ? `${category?.label} › ${sub.label}` : tt("a.4f9b3bb6", "레이에게 물어보기"))}
        </Text>
        {onClose && <Button size="sm" variant="ghost" onClick={onClose}>{tt("a.218e2ae6", "닫기")}</Button>}
      </Flex>
      <StepBar at={step} />

      {step === 0 && (
        <Box>
          {treeError && <Text fontSize="13px" color="status.warning" mb={2}>{tt("a.9055b495", "분류를 불러오지 못했어요. 레이에게 바로 물어봐 주세요.")}</Text>}
          <Box display="grid" gridTemplateColumns={compact ? "1fr 1fr" : { base: "1fr 1fr", md: "repeat(4, 1fr)" }} gap={2}>
            {tree.map((c) => (
              <Button key={c.key} h="auto" py={3} variant="outline" flexDirection="column" gap={1} whiteSpace="normal" onClick={() => pickCategory(c)}>
                <Text fontSize="20px" aria-hidden="true">{c.icon || "💬"}</Text>
                <Text fontSize="13px" fontWeight="700">{c.label}</Text>
              </Button>
            ))}
          </Box>
          <Button mt={3} w="100%" variant="link" colorScheme="brand" onClick={skipTopic}>{tt("a.e32e8038", "잘 모르겠어요 · 레이에게 바로 물어보기")}</Button>
        </Box>
      )}

      {step === 1 && (
        <Box border="1px solid" borderColor="neutral.200" borderRadius="10px" overflow="hidden">
          {(category?.children || []).map((s) => (
            <Flex key={s.key} as="button" type="button" w="100%" textAlign="left" align="center" px={3.5} py={3}
              borderBottom="1px solid" borderColor="neutral.100" _last={{ borderBottom: 0 }} _hover={{ bg: "neutral.50" }}
              color={s.is_urgent ? "status.error" : "neutral.800"} fontWeight={s.is_urgent ? 700 : 500} onClick={() => pickSub(s)}>
              {s.is_urgent && <Box as={FiAlertTriangle} mr={2} aria-hidden="true" />}
              <Text flex="1" fontSize="14px">{s.label}</Text>
              <Box as={FiChevronRight} color="neutral.400" aria-hidden="true" />
            </Flex>
          ))}
        </Box>
      )}

      {step === 2 && (
        <Box>
          {faq === null ? <Flex justify="center" py={6}><Spinner color="brand.500" /></Flex> : (
            <Flex direction="column" gap={2}>
              {faq.length === 0 && <Text fontSize="13px" color="neutral.600">{tt("a.9195ff90", "이 세부에는 아직 자주 묻는 질문이 없어요.")}</Text>}
              {faq.map((f) => (
                <Box key={f.entry_id} border="1px solid" borderColor={openFaq === f.entry_id ? "brand.200" : "neutral.200"} borderRadius="10px" bg="white">
                  <Flex as="button" type="button" w="100%" textAlign="left" align="center" gap={2} px={3.5} py={2.5}
                    onClick={() => toggleFaq(f)} aria-expanded={openFaq === f.entry_id}>
                    <Text flex="1" fontSize="14px" fontWeight="700">{f.question}</Text>
                    <Box as={openFaq === f.entry_id ? FiChevronUp : FiChevronDown} color="neutral.500" aria-hidden="true" />
                  </Flex>
                  {openFaq === f.entry_id && (
                    <Box px={3.5} pb={3}>
                      <LayText content={f.answer_md} />
                      {faqDone ? <Text mt={2} fontSize="13px" color="brand.700">{tt("a.ef17c672", "다행이에요! 다른 것도 궁금하면 이전으로 돌아가 골라 주세요.")}</Text> : (
                        <Flex gap={2} mt={2.5}>
                          <Button size="sm" variant="outline" flex="1" onClick={() => { setFaqDone(true); record("faq_yes", { topic: sub?.key, kbEntryId: f.entry_id }); }}>{tt("a.9d084edf", "해결됐어요")}</Button>
                          <Button size="sm" variant="outline" flex="1" onClick={() => { record("faq_no", { topic: sub?.key, kbEntryId: f.entry_id }); setOpenFaq(null); }}>{tt("a.545cd247", "아니에요")}</Button>
                        </Flex>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
              <Button mt={1} variant="outline" borderStyle="dashed" colorScheme="brand" onClick={() => setStep(3)}>
                {tt("a.f9dda812", "찾는 질문이 없어요 · 레이에게 직접 질문하기")}
              </Button>
            </Flex>
          )}
        </Box>
      )}

      {step === 3 && (
        <Box>
          <Flex direction="column" gap={2.5} mb={3}>
            {messages.length === 0 && !confirming && (
              <Text fontSize="13.5px" color="neutral.600">{tt("a.5b309171", "궁금한 점을 적어 주세요. 레이가 레이블리 이용 가이드에서 찾아 답해요.")}</Text>
            )}
            {messages.map((m, i) => (
              <Flex key={i} justify={m.role === "user" ? "flex-end" : "flex-start"}>
                <Box maxW="85%" px={3} py={2} borderRadius="12px" border="1px solid"
                  bg={m.role === "user" ? "brand.50" : m.error ? "statusWash.warning" : "white"}
                  borderColor={m.role === "user" ? "brand.100" : "neutral.200"}>
                  {m.role === "user" ? <Text fontSize="14px" whiteSpace="pre-wrap">{m.content}</Text> : <LayText content={m.content} />}
                </Box>
              </Flex>
            ))}
            {asking && <Flex align="center" gap={2} color="neutral.500" fontSize="13px"><Spinner size="xs" />{tt("a.69a29467", "레이가 찾아보고 있어요")}</Flex>}
          </Flex>

          {showAnswerButtons && (
            <Flex gap={2} mb={3}>
              <Button size="sm" variant="outline" onClick={markSolved}>{tt("a.9d084edf", "해결됐어요")}</Button>
              <Button size="sm" variant="outline" onClick={() => setReasonOpen(true)}>{tt("a.64436431", "아직 해결 안 됐어요")}</Button>
            </Flex>
          )}
          {solved && <Text fontSize="13px" color="brand.700" mb={3}>{tt("a.92e76ead", "다행이에요! 더 궁금한 게 있으면 이어서 물어보세요.")}</Text>}

          {reasonOpen && (
            <Box border="1px solid" borderColor="neutral.200" borderRadius="12px" p={3} mb={3} bg="white" role="group" aria-label={tt("a.ede8c2cd", "아쉬웠던 점 고르기")}>
              <Text fontSize="14px" fontWeight="700" mb={2}>{tt("a.d1ecd490", "어떤 점이 아쉬웠나요?")}</Text>
              <Flex direction="column" gap={2}>
                {reasons().map((r) => (
                  <Button key={r.value} variant="outline" h="auto" py={2} justifyContent="flex-start" whiteSpace="normal" textAlign="left" onClick={() => chooseReason(r.value)}>
                    <Box><Text fontSize="13.5px" fontWeight="700">{r.title}</Text><Text fontSize="12px" color="neutral.600" fontWeight="400">{r.hint}</Text></Box>
                  </Button>
                ))}
              </Flex>
              <Text fontSize="12px" color="neutral.500" mt={2} textAlign="center">{tt("a.f2e97228", "다시 답을 들어도 해결이 안 되면 상담사 연결이 열려요")}</Text>
            </Box>
          )}

          {confirming && reason && (
            <Box border="1.5px solid" borderColor="brand.400" borderRadius="12px" p={3.5} mb={3} bg="white">
              <Text fontSize="15px" fontWeight="700" mb={2}>{tt("a.c12a1429", "상담사와 연결할게요")}</Text>
              <Flex direction="column" gap={1} fontSize="13px" color="neutral.700" mb={2.5}>
                <Text>{tt("a.2234b661", "✓ 고른 분류와 레이와 나눈 대화를 운영팀에 함께 보내요")}</Text>
                <Text>{tt("a.66e6f423", "✓ 운영 시간은 평일 오전 10시~오후 7시예요(공휴일 제외)")}</Text>
                <Text>{tt("a.d8cd325f", "✓ 답이 오면 알림으로 알려 드려요")}</Text>
              </Flex>
              <Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 2000))} placeholder={tt("a.d00a012e", "더 적고 싶은 내용이 있으면 적어 주세요 (선택)")}
                fontSize="13.5px" rows={2} mb={2.5} aria-label={tt("a.ec8dd7c6", "상담사에게 남길 말")} />
              <Button w="100%" colorScheme="brand" isLoading={connecting} onClick={connect} isDisabled={impersonating}>{tt("a.5b6e9b5c", "연결하기")}</Button>
            </Box>
          )}

          {!confirming && !reasonOpen && (
            <Flex gap={2} as="form" onSubmit={(e) => { e.preventDefault(); const q = input; setInput(""); ask(q); }}>
              <Textarea value={input} onChange={(e) => setInput(e.target.value.slice(0, 500))} placeholder={messages.length ? tt("a.fc3cb475", "더 물어보기") : tt("a.7d2c4f4b", "무엇이 궁금하세요?")}
                rows={1} minH="40px" resize="none" fontSize="14px" aria-label={tt("a.495d3d0b", "레이에게 질문")}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !compact) { e.preventDefault(); const q = input; setInput(""); ask(q); } }} />
              <Button type="submit" colorScheme="brand" isDisabled={!input.trim() || asking} aria-label={tt("a.274cb078", "보내기")}><FiSend /></Button>
            </Flex>
          )}
        </Box>
      )}
      <div ref={bottomRef} />
    </Box>
  );
}
